import uniqBy from 'lodash.uniqby';
import {
    CommerceApiContext,
    Store,
    StoreProps,
    StoreResponse,
} from '~/lib/data-contract/salesforce';
import { COORDINATES_DENMARK } from '~/shared/constants';

type MinimalStore = Pick<Store, 'name' | 'id'>;

export type MinimalStoreResponse = Omit<StoreResponse, 'data'> & {
    data: MinimalStore[] | undefined;
};

const MAX_STORES_PER_PAGE = 200;

export async function getStoreList(
    api: CommerceApiContext,
    isMinimal: true,
): Promise<MinimalStoreResponse | undefined>;
export async function getStoreList(
    api: CommerceApiContext,
    isMinimal?: false,
): Promise<StoreResponse | undefined>;
export async function getStoreList(
    api: CommerceApiContext,
    isMinimal?: boolean,
): Promise<StoreResponse | MinimalStoreResponse | undefined> {
    const parameters: StoreProps = {
        ...COORDINATES_DENMARK,
        count: MAX_STORES_PER_PAGE,
        start: 0, // Indices 0 -> 199
    };

    let result = await api.shopperStores?.getStoreList({
        parameters,
    });

    if (result?.next) {
        const promises = [];

        // Indices 200 -> ...{result.total - 1}
        for (let start = MAX_STORES_PER_PAGE; start < result.total; start += MAX_STORES_PER_PAGE) {
            const params = { ...parameters, start };

            const promise = api.shopperStores?.getStoreList({
                parameters: params,
            });

            promises.push(promise);
        }

        const storeListResult = (await Promise.all(promises)) as StoreResponse[];
        const storeList = storeListResult.reduce((acc, store) => {
            /**
             * Due to an edge case on the GET /stores endpoint of SFCC where all stores are filtered
             * out in pages with smaller datasets, we can get `undefined` on store.data.
             */
            if (!store.data) return acc;

            return [...acc, ...store.data];
        }, [] as Store[]);

        // Dedupe by id — pagination can land the same store on both sides of a page boundary.
        result = {
            ...result,
            data: uniqBy([...result.data, ...storeList], (store) => store.id),
        };
    }

    // We will only forward the store's name and ID when using it minimally.
    if (isMinimal) {
        return {
            ...result,
            data: result?.data.map((store) => ({
                name: store.name,
                id: store.id,
            })),
        };
    }

    return result;
}

export const getStore = async (
    api: CommerceApiContext,
    id: string,
    parameters?: StoreProps,
    delay?: number,
) => {
    if (delay) {
        await new Promise((resolve) => setTimeout(resolve, delay));
    }

    return await api.shopperStores?.getStore({ parameters: { id, ...parameters } });
};
